import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import './RatecardForm.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import SearchableSelect from '../components/SearchableSelect';
import { CURRENCIES, DEFAULT_CURRENCY } from '../config';

/**
 * Parse "50000.00 CZK" → { amount: '50000.00', currency: 'CZK' }
 */
function parsePricePerUnit(val) {
  if (!val) return { amount: '', currency: DEFAULT_CURRENCY };
  const parts = val.trim().split(/\s+/);
  if (parts.length >= 2) {
    const currency = CURRENCIES.includes(parts[parts.length - 1]) ? parts[parts.length - 1] : DEFAULT_CURRENCY;
    const amount = parts.slice(0, -1).join('');
    return { amount, currency };
  }
  return { amount: val.trim(), currency: DEFAULT_CURRENCY };
}

/**
 * Format { amount, currency } → "50000.00 CZK"
 */
function formatPricePerUnit(amount, currency) {
  if (!amount) return null;
  return `${amount} ${currency}`;
}

function RatecardForm({ openTab, closeTab, activeTab, mediaId, mediaName, ratecardId }) {
  const isEditMode = !!ratecardId;

  // Form state
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [isAvailable, setIsAvailable] = useState(1);
  const [mutationId, setMutationId] = useState('');
  const [items, setItems] = useState([]);

  // Lookup data
  const [formats, setFormats] = useState([]);
  const [units, setUnits] = useState([]);
  const [mutations, setMutations] = useState([]);
  const [initialMutationName, setInitialMutationName] = useState(null);

  // Form submission state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Load lookup data (formats + units + mutations of medium)
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [fmtResult, unitResult, mutResult] = await Promise.all([
          api.getRatecardFormats(),
          api.getRatecardUnits(),
          mediaId ? api.getMediumMutations(mediaId) : Promise.resolve({ data: [] }),
        ]);
        setFormats(fmtResult.data || []);
        setUnits(unitResult.data || []);
        setMutations(Array.isArray(mutResult.data) ? mutResult.data : []);
      } catch (err) {
        console.error('Failed to load lookups:', err);
      }
    };
    fetchLookups();
  }, [mediaId]);

  // Load existing ratecard data in edit mode
  useEffect(() => {
    if (!isEditMode) {
      setLoadingData(false);
      return;
    }

    const fetchRatecard = async () => {
      setLoadingData(true);
      try {
        // Get ratecard detail (includes items)
        const result = await api.getRatecard(ratecardId);
        const data = result.data;
        if (data) {
          setName(data.name || '');
          setNote(data.note || '');
          setIsAvailable(data.is_available ? 1 : 0);
          setMutationId(data.mutation_id ?? '');
          setInitialMutationName(data.mutation_name ?? null);

          // Load items with enriched data
          const itemsResult = await api.getRatecardItems(ratecardId);
          const loadedItems = (itemsResult.data?.items || []).map(item => {
            const parsed = parsePricePerUnit(item.price_per_unit);
            return {
              _key: Math.random(),
              name: item.name || '',
              format_id: item.format_id || '',
              units: item.units || '',
              max_count: item.max_count || 0,
              priceAmount: parsed.amount,
              priceCurrency: parsed.currency,
              is_available: item.is_available ? 1 : 0,
            };
          });
          setItems(loadedItems);
          setInitialLoaded(true);
        }
      } catch (err) {
        setError('Nepodařilo se načíst data ceníku.');
        console.error('Failed to load ratecard:', err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchRatecard();
  }, [ratecardId, isEditMode]);

  // Track form changes
  useEffect(() => {
    if (isEditMode && !initialLoaded) return;
    if (name || note || items.length > 0) {
      setIsDirty(true);
    }
  }, [name, note, isAvailable, items, isEditMode, initialLoaded]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm('Máte neuložené změny. Opravdu chcete zavřít formulář?');
      if (!confirmed) return;
    }
    if (closeTab && activeTab) {
      closeTab(activeTab);
    }
  }, [isDirty, closeTab, activeTab]);

  const handleAddItem = () => {
    setIsDirty(true);
    setItems(prev => [...prev, {
      _key: Math.random(),
      name: '',
      format_id: '',
      units: '',
      max_count: 0,
      priceAmount: '',
      priceCurrency: DEFAULT_CURRENCY,
      is_available: 1,
    }]);
  };

  const handleRemoveItem = (index) => {
    setIsDirty(true);
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setIsDirty(true);
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSave = async () => {
    setError('');

    if (!name.trim()) {
      setError('Název ceníku je povinný.');
      return;
    }

    // Validate items
    for (let i = 0; i < items.length; i++) {
      if (!items[i].name.trim()) {
        setError(`Položka #${i + 1}: Název je povinný.`);
        return;
      }
      if (!items[i].units) {
        setError(`Položka #${i + 1} "${items[i].name}": Jednotky jsou povinné.`);
        return;
      }
    }

    setSaving(true);
    try {
      const itemsData = items.map(item => ({
        name: item.name.trim(),
        format_id: item.format_id || null,
        units: item.units || null,
        max_count: parseInt(item.max_count) || 0,
        price_per_unit: formatPricePerUnit(item.priceAmount, item.priceCurrency),
        is_available: item.is_available,
      }));

      const payload = {
        name: name.trim(),
        medium_id: mediaId,
        note: note.trim() || null,
        is_available: isAvailable,
        mutation_id: mutationId === '' ? null : Number(mutationId),
        items: itemsData,
      };

      if (isEditMode) {
        await api.updateRatecard(ratecardId, payload);
        if (closeTab && activeTab) {
          closeTab(activeTab);
        }
        openTab(
          `media-detail-${mediaId}`,
          mediaName || 'Médium',
          `/media-pricelist/${mediaId}`,
          'MediaPricelistDetail',
          { mediaId }
        );
        showToast('Ceník byl úspěšně upraven.', 'success');
        window.dispatchEvent(new CustomEvent('media:refresh', { detail: { mediaId } }));
      } else {
        const result = await api.createRatecard(payload);
        if (closeTab && activeTab) {
          closeTab(activeTab);
        }
        openTab(
          `media-detail-${mediaId}`,
          mediaName || 'Médium',
          `/media-pricelist/${mediaId}`,
          'MediaPricelistDetail',
          { mediaId }
        );
        showToast('Ceník byl úspěšně vytvořen.', 'success');
        window.dispatchEvent(new CustomEvent('media:refresh', { detail: { mediaId } }));
      }
    } catch (err) {
      setError(err.message || `Nepodařilo se ${isEditMode ? 'uložit změny' : 'vytvořit ceník'}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToMediaList = () => {
    openTab('media-pricelist', 'Média a ceníky', '/media-pricelist', 'MediaPricelist');
  };

  const handleGoToMediaDetail = () => {
    if (mediaId) {
      openTab(
        `media-detail-${mediaId}`,
        mediaName || 'Detail média',
        `/media-pricelist/${mediaId}`,
        'MediaPricelistDetail',
        { mediaId }
      );
    }
  };

  if (loadingData) {
    return (
      <Container fluid className="ratecard-form-container">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Načítání dat…</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="ratecard-form-container">

      {/* Breadcrumb */}
      <div className="breadcrumb-custom">
        <a onClick={handleGoToMediaList}>Média a ceníky</a>
        <i className="bi bi-chevron-right" style={{ color: '#6c757d', fontSize: '.85rem' }}></i>
        {mediaName && (
          <>
            <a onClick={handleGoToMediaDetail}>{mediaName}</a>
            <i className="bi bi-chevron-right" style={{ color: '#6c757d', fontSize: '.85rem' }}></i>
          </>
        )}
        <span className="fw-semibold">{isEditMode ? 'Upravit ceník' : 'Nový ceník'}</span>
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Basic info card */}
      <div className="ratecard-form-card">
        <div className="card-header-custom">
          <h5 className="mb-0">{isEditMode ? 'Upravit ceník' : 'Nový ceník'}</h5>
        </div>

        <div className="card-body-custom">
          <div className="form-section">
            <div className="section-title">Základní informace</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Název ceníku <span className="text-danger">*</span></label>
                <Form.Control
                  type="text"
                  placeholder="např. Ceník 2025 – jaro"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Stav</label>
                <Form.Select
                  value={isAvailable}
                  onChange={(e) => { setIsAvailable(parseInt(e.target.value)); setIsDirty(true); }}
                >
                  <option value={1}>Aktivní</option>
                  <option value={0}>Neaktivní</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Mutace</label>
                <Form.Select
                  value={mutationId}
                  onChange={(e) => { setMutationId(e.target.value); setIsDirty(true); }}
                  disabled={mutations.length === 0 && !mutationId}
                >
                  <option value="">— žádná —</option>
                  {/* Pokud je nastavená mutace, která není v seznamu (např. patří jinému médiu), přidat ji navíc, ať je vidět. */}
                  {mutationId && !mutations.find(m => String(m.id) === String(mutationId)) && (
                    <option value={mutationId}>{initialMutationName || `#${mutationId}`}</option>
                  )}
                  {mutations.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-12">
                <label className="form-label">Poznámka</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Volitelná poznámka k ceníku…"
                  value={note}
                  onChange={(e) => { setNote(e.target.value); setIsDirty(true); }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items card */}
      <div className="ratecard-form-card">
        <div className="card-header-custom">
          <div>
            <strong>Položky ceníku</strong>
            <span className="badge bg-secondary ms-2">{items.length}</span>
          </div>
          <Button variant="primary" size="sm" onClick={handleAddItem}>
            <i className="bi bi-plus-lg me-1"></i>Přidat položku
          </Button>
        </div>

        <div className="table-responsive">
          <table className="table table-polozky">
            <thead>
              <tr>
                <th style={{ width: '22%' }}>Název položky *</th>
                <th style={{ width: '18%' }}>Rozměry</th>
                <th style={{ width: '14%' }}>Jednotky *</th>
                <th style={{ width: '10%' }}>Max. kusů</th>
                <th style={{ width: '12%' }}>Cena</th>
                <th style={{ width: '8%' }}>Měna</th>
                <th style={{ width: '10%' }}>Stav</th>
                <th style={{ width: '6%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="empty-state">
                      <i className="bi bi-inbox"></i>
                      <div>Zatím žádné položky</div>
                      <small>Klikněte na "Přidat položku"</small>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item._key}>
                    <td>
                      <Form.Control
                        type="text"
                        placeholder="Název položky…"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        options={formats}
                        value={item.format_id}
                        onChange={(id) => handleItemChange(index, 'format_id', id)}
                        placeholder="Hledat rozměr…"
                        emptyLabel="— bez rozměru —"
                        allowEmpty
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        options={units}
                        value={item.units}
                        onChange={(id) => handleItemChange(index, 'units', id)}
                        placeholder="Hledat jednotku…"
                        emptyLabel="— vyberte —"
                        allowEmpty={false}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        className="text-center"
                        placeholder="0"
                        min="0"
                        value={item.max_count}
                        onChange={(e) => handleItemChange(index, 'max_count', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        placeholder="50000.00"
                        value={item.priceAmount}
                        onChange={(e) => handleItemChange(index, 'priceAmount', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Select
                        value={item.priceCurrency}
                        onChange={(e) => handleItemChange(index, 'priceCurrency', e.target.value)}
                      >
                        {CURRENCIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        value={item.is_available}
                        onChange={(e) => handleItemChange(index, 'is_available', parseInt(e.target.value))}
                      >
                        <option value={1}>Aktivní</option>
                        <option value={0}>Neaktivní</option>
                      </Form.Select>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Odstranit"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="form-footer">
        <div className="form-footer-hint">
          <span className="text-danger">*</span> Povinná pole
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleCancel}>
            Zrušit
          </Button>
          <Button variant="primary" className="px-4" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Ukládám…
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i>
                {isEditMode ? 'Uložit změny' : 'Uložit ceník'}
              </>
            )}
          </Button>
        </div>
      </div>

    </Container>
  );
}

export default RatecardForm;
